import React, { FC, useContext, useEffect, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { useErrorController } from "@natera/platform/lib/hooks";
import {
  ConfigContext,
  ErrorProvider,
  NotificationContext,
  UserContext,
} from "@app/provider";
import { LinkHeap, Notification, SpinnerView } from "@app/components";
import { validateEmail } from "@app/utils";
import { useLocation } from "react-router-dom";
import acknowledgements from "@etc/acknowledgements.json";
import { HEAP_EVENTS, HeapEventLocation } from "@app/provider/types";
import { useRecaptchaSubmit, RecaptchaTokens } from "@app/hooks";
import { CredentialsInterface, UnlockedView } from "./unlockedView";
import UnlockView from "./unlockView";

import "./unlock.scss";

const RECAPTCHA_UNLOCK_ACCOUNT_ACTION = "unlock_account";

const messages = defineMessages({
  unlockInvalidEmail: {
    id: "unlockInvalidEmail",
    defaultMessage: "Please enter a valid email address. (username@domain)",
  },
  unlockLastNameRequired: {
    id: "unlockLastNameRequired",
    defaultMessage: "Last name is required.",
  },
  unlockDateOfBirthRequired: {
    id: "unlockDateOfBirthRequired",
    defaultMessage: "Date of birth is required.",
  },
  unlockGenericError: {
    id: "unlockGenericError",
    defaultMessage: "We are unable to verify your identity. {support}",
  },
  unlockContactSupport: {
    id: "unlockContactSupport",
    defaultMessage: "Contact Support",
  },
});

interface LocationState {
  email: string;
}

const Unlock: FC = () => {
  const intl = useIntl();
  const location = useLocation();
  const locationState = location.state as LocationState;

  const { config } = React.useContext(ConfigContext);
  const { clear } = useContext(NotificationContext);
  const {
    unlockUser,
    isLoading: unlockContextLoading,
    unlockedUser,
    clearUnlockUserData,
  } = useContext(UserContext);

  const {
    acquireTokens,
    handleRecaptchaError,
    promoteToV2Challenge,
    resetV2Challenge,
    v2Challenge,
    RecaptchaV3UnavailableError,
  } = useRecaptchaSubmit({
    action: RECAPTCHA_UNLOCK_ACCOUNT_ACTION,
    v2ContainerClassName: "unlock__recaptcha-v2",
    v2TestId: "unlock-recaptcha-v2",
  });

  const usePlusSignInEmailAddress =
    config.test.usePlusSignInEmailAddress.enabled;

  const errorController = useErrorController();

  const [email, setEmail] = useState(locationState?.email);
  const [sent, setSent] = useState(false);
  const [hasServerError, setHasServerError] = useState(false);
  const [credentials, setCredentials] = useState<CredentialsInterface>();
  const [localLoading, setLocalLoading] = useState(false);

  const isLoading = unlockContextLoading || localLoading;

  useEffect(() => {
    clear();
    clearUnlockUserData();

    return () => clear();
  }, []);

  const submitUnlockUser = async (
    emailValue: string,
    dateOfBirth?: string,
    lastName?: string,
    tokensOverride?: RecaptchaTokens
  ) => {
    setLocalLoading(true);
    let captchaTokens: RecaptchaTokens;
    try {
      captchaTokens = tokensOverride ?? (await acquireTokens());
    } catch (err) {
      setLocalLoading(false);
      if (err instanceof RecaptchaV3UnavailableError) {
        promoteToV2Challenge(err.reason, async (tokens) => {
          await submitUnlockUser(emailValue, dateOfBirth, lastName, tokens);
        });
        return;
      }
      setHasServerError(true);
      return;
    }

    try {
      await unlockUser(emailValue, dateOfBirth, lastName, captchaTokens);
      setCredentials({ email: emailValue, dateOfBirth, lastName });
      resetV2Challenge();
    } catch (err) {
      const handled = handleRecaptchaError(err, async (tokens) => {
        await submitUnlockUser(emailValue, dateOfBirth, lastName, tokens);
      });
      if (!handled) {
        setHasServerError(true);
      }
    } finally {
      setLocalLoading(false);
    }
  };

  const handleSubmit = (
    emailValue: string,
    dateOfBirth?: string,
    lastName?: string
  ) => {
    errorController.clearValidationError("email");
    errorController.clearValidationError("lastName");
    errorController.clearValidationError("dateOfBirth");
    setHasServerError(false);
    setEmail(emailValue);

    let hasErrors = false;

    if (!validateEmail(emailValue, usePlusSignInEmailAddress)) {
      errorController.setValidationError(
        "email",
        intl.formatMessage(messages.unlockInvalidEmail)
      );
      hasErrors = true;
    }

    if (!lastName) {
      errorController.setValidationError(
        "lastName",
        intl.formatMessage(messages.unlockLastNameRequired)
      );
      hasErrors = true;
    }

    if (!dateOfBirth) {
      errorController.setValidationError(
        "dateOfBirth",
        intl.formatMessage(messages.unlockDateOfBirthRequired)
      );
      hasErrors = true;
    }

    if (hasErrors) return;

    void submitUnlockUser(emailValue, dateOfBirth, lastName);
  };

  useEffect(() => {
    if (!unlockedUser) {
      setSent(false);
      setEmail(locationState?.email);
      clear();
    } else if (
      unlockedUser.success &&
      unlockedUser.email &&
      email === unlockedUser.email
    ) {
      setSent(true);
    }
  }, [unlockedUser]);

  const errorNotification = hasServerError ? (
    <Notification type="error" role="alert">
      {intl.formatMessage(messages.unlockGenericError, {
        support: (
          <LinkHeap
            target="_blank"
            key="contactSupport"
            rel="noreferrer"
            to={acknowledgements.links.contactUs}
            heapEventName={HEAP_EVENTS.upp_click_contactnatera}
            heapEventProps={{
              location: HeapEventLocation.verification_unlock_page,
            }}
          >
            {intl.formatMessage(messages.unlockContactSupport)}
          </LinkHeap>
        ),
      })}
    </Notification>
  ) : undefined;

  if (sent) {
    return (
      <>
        <SpinnerView isLoading={isLoading} />
        <UnlockedView email={email} credentials={credentials} />
      </>
    );
  }

  return (
    <article className="unlock__container">
      <SpinnerView isLoading={isLoading} />
      <ErrorProvider controller={errorController}>
        <UnlockView
          onSubmit={handleSubmit}
          email={email || ""}
          errorNotification={errorNotification}
        />
        {v2Challenge}
      </ErrorProvider>
    </article>
  );
};

export default Unlock;
